import { apiService } from './api';
import { PassportRecord, ApplicationStatus, LatestStatus, StatusHistory, AuditLogSearchParams, DailySummarySearchParams,
  StatusHistoryResponse, SearchParams, ChangeStatusData, DashboardStats, OfficerStats, ManagerStats, ServiceName } from '../types/index';

class PassportService {
  
  async getServiceNames(): Promise<ServiceName[]> {
    const response = await apiService.get('/passport/service-names/');
    return Array.isArray(response) ? response : (response.results ?? []);
  }
  /**
   * Create a new passport applicant record
   */

  async createRecord(data: PassportRecord): Promise<PassportRecord> {
    const response = await apiService.post('/passport/records/', data);
    return response;
  }

  /**
   * Get records created by the current user
   */
  async getMyRecords(params: SearchParams = {}) {
    // Remove empty params to avoid "Invalid page" error
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
    const response = await apiService.get('/passport/records/my_records/', { params: cleanParams });
    return response;
  }

  /**
   * Get all records (for managers)
   */
  async getAllRecords(params: SearchParams = {}) {
    // Remove empty params to avoid "Invalid page" error
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
    const response = await apiService.get('/passport/records/all_records/', { params: cleanParams });
    return response;
  }

  /**
   * Get a single record by ID
   */
  async getRecordById(id: number): Promise<PassportRecord> {
    const response = await apiService.get(`/passport/records/${id}/`);
    return response;
  }

  /**
   * Update a passport applicant record
   */
  async updateRecord(id: number, data: Partial<PassportRecord>): Promise<PassportRecord> {
    const response = await apiService.put(`/passport/records/${id}/`, data);
    return response;
  }

  /**
   * Partially update a passport applicant record
   */
  async patchRecord(id: number, data: Partial<PassportRecord>): Promise<PassportRecord> {
    const response = await apiService.patch(`/passport/records/${id}/`, data);
    return response;
  }

  /**
   * Delete a record (soft delete by changing status)
   */
  async deleteRecord(id: number): Promise<void> {
    await apiService.delete(`/passport/records/${id}/`);
  }

  /**
   * Change the status of a record
   */
  async changeStatus(id: number, data: ChangeStatusData): Promise<PassportRecord> {
    const response = await apiService.post(`/passport/records/${id}/change_status/`, data);
    return response;
  }

  /**
   * Get status history for a record
   */
  async getStatusHistory(id: number): Promise<StatusHistoryResponse> {
    const response = await apiService.get(`/passport/records/${id}/status_history/`);
    return response.data;
  }

  /**
   * Search records with advanced filters
   */
  async searchRecords(params: SearchParams = {}) {
    const response = await apiService.get('/passport/records/search/', { params });
    return response.data;
  }

  /**
   * Export records to Excel or PDF
   */
  async exportRecords(params: SearchParams = {}, format: 'excel' | 'pdf' = 'excel') {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
    const fileExtension = format === 'pdf' ? 'pdf' : 'xlsx';
    return await apiService.download(
      '/passport/records-export/',
      `passport_records_${new Date().getTime()}.${fileExtension}`,
      { ...cleanParams, format },
    );
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiService.get('/dashboard/stats/');
    return response.data;
  }

  /**
   * Get officer statistics
   */
  async getOfficerStats(officerId?: number): Promise<OfficerStats> {
    const url = officerId 
      ? `/dashboard/officer-stats/${officerId}/`
      : '/dashboard/officer-stats/';
    const response = await apiService.get(url);
    return response.data;
  }

  /**
   * Get manager statistics
   */
  async getManagerStats(): Promise<ManagerStats> {
    const response = await apiService.get('/dashboard/manager-stats/');
    return response.data;
  }

  /**
   * Get records by status
   */
  async getRecordsByStatus(status: ApplicationStatus, params: SearchParams = {}) {
    return this.searchRecords({ ...params, status });
  }

  /**
   * Get pending records
   */
  async getPendingRecords(params: SearchParams = {}) {
    return this.getRecordsByStatus('PENDING', params);
  }

  /**
   * Get verified records
   */
  async getVerifiedRecords(params: SearchParams = {}) {
    return this.getRecordsByStatus('VERIFIED', params);
  }

  /**
   * Get rejected records
   */
  async getRejectedRecords(params: SearchParams = {}) {
    return this.getRecordsByStatus('DELETED', params);
  }

  /**
   * Verify a record (change status to VERIFIED)
   */
  async verifyRecord(id: number, remarks?: string): Promise<PassportRecord> {
    return this.changeStatus(id, { status: 'VERIFIED', remarks });
  }

  /**
   * Reject a record (change status to REJECTED)
   */
  async rejectRecord(id: number, remarks?: string): Promise<PassportRecord> {
    return this.changeStatus(id, { status: 'DELETED', remarks });
  }

  /**
   * Get expiring iqamas
   */
  async getExpiringIqamas(days: number = 30) {
    // Calculate date range
    const today = new Date();
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + days);

    return this.searchRecords({
      from_date: today.toISOString().split('T')[0],
      to_date: futureDate.toISOString().split('T')[0],
    });
  }

  /**
   * Get records by applicant type
   */
  async getRecordsByApplicantType(
    applicantType: 'INDIVIDUAL' | 'ORGANIZATION',
    params: SearchParams = {}
  ) {
    return this.searchRecords({ ...params, applicants_type: applicantType });
  }

  /**
   * Get records by passport type
   */
  async getRecordsByPassportType(
    passportType: 'MRP' | 'EPP',
    params: SearchParams = {}
  ) {
    return this.searchRecords({ ...params, passport_type: passportType });
  }
  /**
   * Get register report (only VERIFIED records)
   * For DATA_ENTRY_OFFICER: own records
   * For DATA_MANAGER/LEAD_MANAGER: all records
   */
  async getRegisterReport(params: SearchParams = {}) {
    // Remove empty params
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
    const response = await apiService.get('/passport/register-report/search/', { params: cleanParams });
    return response;
  }

  async exportRegisterReport(params: SearchParams & { format?: 'excel' | 'pdf' } = {}) {
    // Remove empty params
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
    
    // Add format to params (default to excel)
    const format = params.format || 'excel';
    const finalParams = { ...cleanParams, format };
    
    // Map format to file extension
    const fileExtension = format === 'excel' ? 'xlsx' : 'pdf';
    
    // Use the explicit FBV endpoint for export
    return await apiService.download(
      '/passport/register-report-export/',
      `register_report_${new Date().getTime()}.${fileExtension}`,
      finalParams
    );
  }

  /**
 * Get audit log (status history for all records)
 * For DATA_ENTRY_OFFICER: own records
 * For DATA_MANAGER/LEAD_MANAGER: all records
 */
async getAuditLog(params: AuditLogSearchParams = {}) {
  // Remove empty params
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
  );
  const response = await apiService.get('/passport/audit-log/search/', { params: cleanParams });
  return response;
}

/**
 * Export audit log to Excel or PDF
 * Only accessible by DATA_MANAGER
 */
async exportAuditLog(params: AuditLogSearchParams & { format?: 'excel' | 'pdf' } = {}) {
  // Remove empty params
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
  );
  
  // Add format to params (default to excel)
  const format = params.format || 'excel';
  const finalParams = { ...cleanParams, format };
  
  // Map format to file extension
  const fileExtension = format === 'excel' ? 'xlsx' : 'pdf';
  
  // Use the explicit FBV endpoint for export
  return await apiService.download(
    '/passport/audit-log-export/',
    `audit_log_${new Date().getTime()}.${fileExtension}`,
    finalParams
  );
}
/**
 * Get daily summary
 * For DATA_ENTRY_OFFICER: own records
 * For DATA_MANAGER/LEAD_MANAGER: all records
 */
async getDailySummary(params: DailySummarySearchParams = {}) {
  // Remove empty params
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
  );
  const response = await apiService.get('/passport/daily-summary/search/', { params: cleanParams });
  return response;
}


}

export const passportService = new PassportService();